import { useState } from 'react';
import { Button, TextField, Box } from '@mui/material';
import { useUsers } from '../../../hooks/api/useUsers';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { ModalDialog } from '../../../components/ModalDialog';
import { StyledInputLabel } from '../../../components/inputs/InputLabel';
import { AlertToast } from '../../../components/AlertToast';

export type SimpleDialogProps = {
  text: string;
  disabled?: boolean;
};

export const AddAdminDialog = ({ text, disabled }: SimpleDialogProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const [showToast, setShowToast] = useState(false);

  const { addUser } = useUsers();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    try {
      setEmailError(false);
      setApiError('');

      if (!email || !validateEmail(email)) {
        setEmailError(true);
      }

      if (email && validateEmail(email)) {
        await addUser({ data: { email } });
        setOpen(false);
        setEmail('');
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 1600);
      }
    } catch (error: unknown) {
      // makeApiRequest throws the response body ({ errors: [...] }), not the
      // axios error.
      const message = (error as { errors?: { message?: string }[] } | null)
        ?.errors?.[0]?.message;
      if (message === 'already exists') {
        setApiError('A user with that email already exists.');
      } else {
        setApiError('Something went wrong when creating a new user.');
      }
    }
  };

  return (
    <>
      <div>
        <Button
          variant="primary"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {text} <PersonAddIcon sx={{ marginLeft: '8px' }} />
        </Button>
        <ModalDialog
          open={open}
          onClose={() => {
            setOpen(false);
            setEmail('');
            setEmailError(false);
            setApiError('');
          }}
          title="Add Admin"
          subtitle="Add a new admin by entering their email address and click Add Admin"
          maxWidth="sm"
          actions={
            <>
              <Button
                variant="default"
                onClick={() => {
                  setOpen(false);
                  setEmail('');
                  setEmailError(false);
                  setApiError('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit} variant="primary">
                Add Admin
              </Button>
            </>
          }
        >
          <Box>
            <StyledInputLabel htmlFor="email">
              Email Address <span aria-hidden="true">*</span>
            </StyledInputLabel>
            <TextField
              required
              id="email"
              name="email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError || !!apiError}
              helperText={
                emailError
                  ? 'Please enter a valid email address.'
                  : apiError || ''
              }
              placeholder="name@domain.com"
            />
          </Box>
        </ModalDialog>
      </div>
      {showToast && (
        <AlertToast severity="success">
          User has been added successfully!
        </AlertToast>
      )}
    </>
  );
};
